import React, { ReactElement } from 'react';

import { Tooltip } from '@mui/material';
import { GridComparatorFn, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { ArmyCalc, Ranks } from '../../../@types/Calculations';
import {
  ArmiesAway,
  Book,
  Building,
  DefPointsSummary,
  Province,
  Som,
  Sot,
  Spell,
  UniqueAbility,
} from '../../../@types/KingdomResponse';
import { RacePersona } from '../../../@types/RacesResponse';
import { Objective } from '../../../@types/State';
import { svgStyles } from '../../../Constants';
import { effects, getEffect } from '../../../Effects';
import {
  getArmyCalcHome,
  getAttrMod,
  getModDef,
  getRacePersonaFromStorage,
  getRawDef,
} from '../../../helpers/Calculations';
import {
  getColorFromAge,
  getColorFromCooldownTicks,
} from '../../../helpers/ColorFormatting';
import {
  DEFAULT_ELITE_VALUE,
  DEFAULT_HORSE_VALUE,
  DEFAULT_SOLD_DEF_VALUE,
  DEFAULT_SOLD_OFF_VALUE,
  DEFAULT_SPEC_VALUE,
} from '../../../helpers/GameAgeData';
import {
  letterize,
  removeDecimalsToString,
} from '../../../helpers/NumberFormatting';
import { ReactComponent as PlagueSVG } from '../../../svgs/effects/plague.svg';

export const getCombo = (row: Province) => {
  if (row.sot) {
    const splitPersonality = row.sot.personality.split(' ');
    const combo =
      splitPersonality.length > 1
        ? `${splitPersonality[0].slice(0, 1)}${splitPersonality[1].slice(0, 1)}`
        : `${first2(row.sot.personality)}`;
    return `${first2(row.race)}/${combo}`;
  } else {
    return `${first2(row.race)}`;
  }
};

// export const getName = (row: Province) => {
//   return (
//     <div>
//       {row.sot?.ruler ? (
//         <Tooltip title={row.sot.ruler} arrow>
//           <span>{row.name}</span>
//         </Tooltip>
//       ) : (
//         <span>{row.name}</span>
//       )}
//     </div>
//   );
// };

export const getSvgItemWithTooltip = (
  component: ReactElement<any, any>,
  text: string
) => {
  return (
    <Tooltip key={text} title={text} arrow>
      {component}
    </Tooltip>
  );
};

export const first2 = (value: string) => {
  return value ? value.slice(0, 2) : '';
};

export const convertSpellNameToUpper = (spellName: string) => {
  return spellName.replaceAll(' ', '').replaceAll("'", '').toUpperCase();
};

export const getEffectFromSpell = (spellName: string) => {
  const upperName = convertSpellNameToUpper(spellName);
  return effects[upperName];
};

export const getRequests = (params: GridRenderCellParams) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {params.row?.requiredOps?.map((op: string) => {
        return renderSpell({ name: op } as Spell);
      })}
    </div>
  );
};

export const getObjectives = (row: any) => {
  return row?.requiredObjectives?.map((ob: Objective) => {
    const effect = getEffectFromSpell(ob.type);
    const name = effect ? effect.shortName : ob.type;
    return `${name}: ${ob.value}`;
  });
};

export const getGoodSpells = (params: GridRenderCellParams) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        padding: 0,
      }}
    >
      {params.row?.sot?.goodSpells
        ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
        .map((x: Spell) => {
          return renderSpell(x);
        })}
    </div>
  );
};

export const getBadSpells = (params: GridRenderCellParams) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {params.row?.sot?.plague &&
        getSvgItemWithTooltip(<PlagueSVG style={{ ...svgStyles }} />, 'Plague')}
      {params.row?.sot?.badSpells
        ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
        .map((x: Spell) => renderSpell(x))}
    </div>
  );
};

export const getGoodShortSpellNames = (row: any) => {
  const spellNames = row?.sot?.goodSpells
    ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
    .map((x: Spell) => {
      const effect = getEffectFromSpell(x.name);
      return effect?.shortName ?? x.name;
    })
    .join(', ');
  return spellNames;
};

export const getBadShortSpellNames = (row: any) => {
  const spellNames = row?.sot?.badSpells
    ?.sort((a: any, b: any) => a.name.localeCompare(b.name))
    .map((x: Spell) => {
      const effect = getEffectFromSpell(x.name);
      return effect?.shortName ?? x.name;
    })
    .join(', ');
  return spellNames;
};

export const renderSpell = (spell: Spell) => {
  const effect = effects[spell.name];
  const label = spell.ticks
    ? `${effect?.displayName ?? spell.name} (${spell.ticks})`
    : effect?.displayName ?? spell.name;
  const svg = getEffect(spell.name, spell.ticks);
  if (!svg) {
    console.log(`${spell.name} was not found in Effects`);
  }
  return svg ? getSvgItemWithTooltip(svg, label) : null;
};

export function getArmy(params: GridRenderCellParams) {
  if (
    params.row.som?.armiesAway.some((a: ArmiesAway) => a.secondsRemaining > 0)
  ) {
    const tooltip = getArmyToolTip(params.row.som.armiesAway);
    const totalIncoming = params.row.som.armiesAway.reduce(
      (accumulator: number, army: ArmiesAway) => accumulator + army.land,
      0
    );
    return (
      <Tooltip title={tooltip} arrow>
        <div style={{ fontSize: '87.5%' }}>
          {getFirstArmy(params.row.som.armiesAway)} ({totalIncoming}a)
        </div>
      </Tooltip>
    );
  } else {
    return null;
  }
}

export function getCooldown(params: GridRenderCellParams) {
  const sot = params.row.sot;
  if (!sot?.cooldown?.length) return undefined;
  const abilities: UniqueAbility[] = sot.cooldown;
  const spans: any[] = [];
  abilities.forEach((x: UniqueAbility, i: number) => {
    const color = getColorFromCooldownTicks(x.cooldown);
    spans.push(
      <div style={{ display: 'inline-block', fontSize: '87.5%' }} key={x.name}>
        {i > 0 ? ', ' : ''}
        {x.name}:
        <span style={{ color: color, marginLeft: '3px' }}>
          {x.cooldown === 0 ? 'OK' : removeDecimalsToString(x.cooldown, 2)}
        </span>
      </div>
    );
  });
  return <div>{spans}</div>;
}

export function getAllArmy(params: GridRenderCellParams) {
  if (
    params.row.som?.armiesAway.some((a: ArmiesAway) => a.secondsRemaining > 0)
  ) {
    let arr = '';
    params.row.som.armiesAway
      .filter((x: ArmiesAway) => x.secondsRemaining > 0)
      .forEach((a: ArmiesAway, i: number) => {
        arr += `${i > 0 ? '|' : ''}${hoursMinutesFromSeconds(
          a.secondsRemaining
        )}(${a.land}a)`.replaceAll(' ', '');
      });
    return <div style={{ fontSize: '87.5%' }}>{arr}</div>;
  } else {
    return null;
  }
}

export function getFirstArmySecondsFromParam(row: Province) {
  const armiesAway = !!row.som?.armiesAway.some(
    (a: ArmiesAway) => a.secondsRemaining > 0
  );
  if (!armiesAway) {
    return 0;
  }
  const first = row.som?.armiesAway.toSorted(
    (a: ArmiesAway, b: ArmiesAway) => a.secondsRemaining - b.secondsRemaining
  )[0];
  return first?.secondsRemaining;
}

function getFirstArmySeconds(armiesAway: ArmiesAway[]) {
  if (armiesAway.length === 0) return 0;
  const first = armiesAway.toSorted(
    (a: ArmiesAway, b: ArmiesAway) => a.secondsRemaining - b.secondsRemaining
  )[0];
  return first.secondsRemaining;
}

export function getFirstArmy(armiesAway: ArmiesAway[]) {
  if (armiesAway.length === 0) return '';
  const seconds = getFirstArmySeconds(armiesAway);
  return hoursMinutesFromSeconds(seconds);
}

function getArmyToolTip(armiesAway: ArmiesAway[]) {
  const arr: ReactElement[] = [];
  const ordered = armiesAway.toSorted(
    (a: ArmiesAway, b: ArmiesAway) => a.secondsRemaining - b.secondsRemaining
  );
  ordered.forEach((a: ArmiesAway, i: number) => {
    arr.push(
      <div>
        {`${hoursMinutesFromSeconds(a.secondsRemaining)}(${a.land}a) bush: ${
          a.ambush
        }`}
      </div>
    );
  });
  return arr;
}

export function getAllIntelAge(params: GridRenderCellParams) {
  const separator = <div style={{ padding: '0 2px' }}>{'|'}</div>;
  return (
    <div style={{ display: 'flex' }}>
      {getIntelAgeWithToolTip(params.row?.sot?.ageSeconds, 'SOT')}
      {separator}
      {getIntelAgeWithToolTip(params.row?.som?.ageSeconds, 'SOM')}
      {separator}
      {getIntelAgeWithToolTip(params.row?.survey?.ageSeconds, 'Survey')}
      {separator}
      {getIntelAgeWithToolTip(params.row?.sos?.ageSeconds, 'SOS')}
    </div>
  );
}

// export function getAllIntelReliability(params: GridRenderCellParams) {
//   const separator = <div style={{ padding: '0 2px' }}>{'|'}</div>;
//   const sot = params.row?.sot?.reliability;
//   const survey = params.row?.survey?.reliability;
//   const som = params.row?.som?.reliability;
//   const sos = params.row?.sos?.reliability;
//   return (
//     <div style={{ display: 'flex' }}>
//       {getReliabilityFromVal(sot, 'SOT')}
//       {separator}
//       {getReliabilityFromVal(som, 'SOM')}
//       {separator}
//       {getReliabilityFromVal(survey, 'Sur')}
//       {separator}
//       {getReliabilityFromVal(sos, 'SOS')}
//     </div>
//   );
// }

// export const getReliabilityFromVal = (
//   val: number | undefined,
//   label: string
// ) => {
//   return (
//     <div
//       style={{
//         color: getColorFromReliability(val),
//         display: 'flex',
//         justifyContent: 'center',
//       }}
//     >
//       {`${label} ${val ?? 0}`}
//     </div>
//   );
// };

export function hoursMinutesFromSeconds(remainingSeconds: number) {
  const hours = Math.floor(remainingSeconds / (60 * 60));
  remainingSeconds = remainingSeconds - hours * 60 * 60;
  const minutes = Math.floor(remainingSeconds / 60);
  remainingSeconds = remainingSeconds - minutes * 60;
  const displayHours = hours ? `${hours}h ` : '';
  const displayMins = minutes ? `${minutes}m ` : '';
  const displaySecs =
    remainingSeconds > 0 && hours === 0 ? `${remainingSeconds}s` : '';
  return `${displayHours}${displayMins}${displaySecs}`;
}

export const getIntelAge = (val: number | undefined) => {
  if (!val) return '-';
  const useDays = val > 86400;
  const useHours = val > 3600;
  const useMins = val > 60;
  const valueToDisplay = isNaN(val)
    ? '-'
    : useDays
    ? `${removeDecimalsToString(val / 86400, 0)}d`
    : useHours
    ? `${removeDecimalsToString(val / 3600, 0)}h`
    : useMins
    ? `${removeDecimalsToString(val / 60, 0)}m`
    : `${removeDecimalsToString(val, 0)}s`;
  return valueToDisplay;
};

export const getIntelAgeWithToolTip = (
  val: number | undefined,
  text: string
) => {
  if (!val) return '';
  const valueToDisplay = getIntelAge(val);
  return (
    <Tooltip title={text} arrow>
      <div
        style={{
          color: getColorFromAge(val),
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {valueToDisplay}
      </div>
    </Tooltip>
  );
};

export const getArmyOffense = (
  army: ArmiesAway,
  racePersona: RacePersona | undefined
) => {
  return (
    army.horses * getAttrMod(racePersona, 'HorseOff', DEFAULT_HORSE_VALUE) +
    army.oSpecs * getAttrMod(racePersona, 'OffSpecOff', DEFAULT_SPEC_VALUE) +
    army.elites * getAttrMod(racePersona, 'EliteOff', DEFAULT_ELITE_VALUE) +
    army.solds * getAttrMod(racePersona, 'SoldierOff', DEFAULT_SOLD_OFF_VALUE)
  );
};

export const getArmyDefense = (
  army: ArmiesAway,
  racePersona: RacePersona | undefined
) => {
  return (
    army.elites * getAttrMod(racePersona, 'EliteDef', DEFAULT_ELITE_VALUE) +
    army.solds * getAttrMod(racePersona, 'SoldierDef', DEFAULT_SOLD_DEF_VALUE)
  );
};

export function hoursMinutesFromDecimal(decimalTimeString: number) {
  const decimalTime = decimalTimeString;
  let remainingSeconds = decimalTime * 60 * 60;
  const hours = Math.floor(remainingSeconds / (60 * 60));
  remainingSeconds = remainingSeconds - hours * 60 * 60;
  const minutes = Math.floor(remainingSeconds / 60);
  return `${hours}h ${minutes}m`;
}

export function getDateAgeTime(val: number) {
  return `${hoursMinutesFromDecimal(val)}`;
}

export function getStyledDateAgeTime(val: number) {
  return (
    <div
      style={{
        color: getColorFromAge(val),
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {`${hoursMinutesFromDecimal(val)}`}
    </div>
  );
}

export const getSurveyValue = (row: any, field: string): number => {
  const buildType: Building = row?.survey?.buildings.find(
    (x: any) => x.name === field
  );
  if (!buildType) return 0;
  return buildType.pctCurr;
};

export const getSurveyRenderValue = (
  params: GridRenderCellParams,
  field: string
) => {
  const total = getSurveyValue(params.row, field);
  return total ? `${total.toFixed(1)}%` : '';
};

export const getSurveyIncomingRenderValue = (
  params: GridRenderCellParams,
  field: string
) => {
  const getSurveyIncomingValue = (
    params: GridRenderCellParams,
    field: string
  ): number => {
    const buildType: Building = params.row?.survey?.buildings.find(
      (x: any) => x.name === field
    );
    if (!buildType) return 0;
    return buildType.pctInc;
  };
  const total = getSurveyIncomingValue(params, field);
  return total ? `${total.toFixed(1)}%` : '';
};

export const getSciValue = (row: any, field: string) => {
  const bookType = row?.sos?.books.find((x: Book) => x.type === field);
  if (!bookType) return '';
  const val = bookType.effect;
  return val ?? 0;
};

export const getSciPercentage = (
  params: GridRenderCellParams,
  field: string
) => {
  const val = getSciValue(params.row, field);
  return val ? `${val}%` : '';
};

export const getBuildingColName = (s: string) => {
  switch (s) {
    case 'Military Barracks':
      return 'Rax';
    case 'Armouries':
      return 'Arms';
    case 'Guard Stations':
      return 'GS';
    case 'Training Grounds':
      return 'TGs';
    case "Thieves' Dens":
      return 'TDs';
    case 'Watch Towers':
      return 'WTs';
    case 'Libraries':
      return 'Libs';
    default:
      return s.substring(0, 4);
  }
};

export const numberWithCommasComparator: GridComparatorFn<string> = (
  v1,
  v2
) => {
  const num1: number = +v1.replace(/,/g, '');
  const num2: number = +v2.replace(/,/g, '');
  return num1 - num2;
};

export const simpleComparator: GridComparatorFn<number> = (v1, v2) => {
  return v1 - v2;
};

export const opComparator: GridComparatorFn<Array<string> | null> = (
  v1,
  v2
) => {
  console.log('opComparator', v1, v2);
  const v1Length = v1?.length ?? 0;
  const v2Length = v2?.length ?? 0;
  return v1Length === v2Length ? 0 : v1Length > v2Length ? 1 : -1;
};

export const percentageComparator: GridComparatorFn<string> = (v1, v2) => {
  const num1: number = +v1.replace(/%/g, '');
  const num2: number = +v2.replace(/%/g, '');
  return num1 - num2;
};

export function getColorizedDefHomeWithTooltip(params: GridRenderCellParams) {
  const summary: DefPointsSummary = params.row.calcs?.defPointsSummary;
  const defense = summary?.defPointsHome ?? undefined;
  const result = colorCodedResult(letterize(defense), summary?.ageSeconds ?? 0);
  const sodDefense =
    params.row.sot?.defPointsHome ??
    (params.row.som?.sodAgeSeconds && params.row.som?.defPointsHome) ??
    '?';
  const sodAgeSeconds = getIntelAge(
    params.row.sot?.sodAgeSeconds ?? params.row.som?.sodAgeSeconds
  );
  const sotAgeSeconds = getIntelAge(params.row.sot?.ageSeconds);
  const somAgeSeconds = getIntelAge(params.row.som?.ageSeconds);
  const tooltip = (
    <div>
      <div>Source Used: {summary?.source}</div>
      <div>
        SoD Def: {sodDefense} ({sodAgeSeconds ?? '-'})
      </div>
      <div>
        SoM Def: {params.row.som?.defPointsHome ?? '?'} ({somAgeSeconds ?? '-'})
      </div>
      <div>
        SoT Def: {params.row.sot?.defPoints ?? '?'} ({sotAgeSeconds ?? '-'})
      </div>
    </div>
  );
  return (
    <Tooltip title={tooltip} arrow>
      <div style={{ textTransform: 'lowercase' }}>{result}</div>
    </Tooltip>
  );
}

export function colorCodedLetterizedResult(
  result: number | undefined | null,
  seconds: number | undefined
) {
  return (
    <div
      style={{
        color: getColorFromAge(seconds),
        display: 'flex',
        justifyContent: 'center',
        textTransform: 'lowercase',
      }}
    >
      {letterize(result)}
    </div>
  );
}

export function colorCodedResult(
  result: number | string | null | undefined,
  seconds: number | undefined
) {
  return <span style={{ color: getColorFromAge(seconds) }}>{result}</span>;
}

export function colorCodedResultBuildingSpan(
  result: number | string | null | undefined,
  inc: string | undefined | null,
  seconds: number | undefined
) {
  return (
    <span
      style={{
        color: getColorFromAge(seconds),
        textAlign: 'left',
      }}
    >
      {result}
      {inc && <span style={{ fontSize: 13 }}>{` (+${inc})`}</span>}
    </span>
  );
}

export function getEstDefHome(params: GridRenderCellParams) {
  // experimental
  const som: Som = params.row.som;
  const sot: Sot = params.row.sot;
  if (!som && !sot) return 0;
  if (!som) return sot.defPoints;
  if (!sot) return som.defPointsHome;
  const racePersona = getRacePersonaFromStorage(
    params.row.race,
    sot.personality
  );
  const armyCalc: ArmyCalc = getArmyCalcHome(sot, som);
  const dme = Math.round(som.dme * 100);
  const rawDef = getRawDef(racePersona, armyCalc, false);
  const modDef = getModDef(rawDef, dme, sot.land);
  return modDef;
}
export const ranks: Ranks = {
  Peasant: 0,
  King: 9,
  Queen: 9,
  Knight: 1,
  Lady: 1,
  Lord: 2,
  'Noble Lady': 2,
  Baron: 3,
  Baroness: 3,
  Viscount: 4,
  Viscountess: 4,
  Count: 5,
  Countess: 5,
  Marquis: 6,
  Marchioness: 6,
  Duke: 7,
  Duchess: 7,
  Prince: 8,
  Princess: 8,
};
